/* ═══════════════════════════════════════════════════════
   src/content/domains/cards/dread.tsx
   Dread domain cards — updated to Void 1.5.
   ═══════════════════════════════════════════════════════ */

import { DomainCard } from './types';
import {
  STRESS,
  HOPE,
  FEAR,
  VERY_CLOSE,
  CLOSE,
  MELEE,
  FAR,
  LONG_REST,
  HIT_POINT,
  REACTION_ROLL,
  VULNERABLE,
  ARMOR_SLOTS,
} from '../../templates';

const dreadCards: DomainCard[] = [

  // ── Level 1 ────────────────────────────────────────────
  {
    name: 'Blighting Strike',
    level: 1,
    domain: 'Dread',
    type: 'Spell',
    recallCost: 1,
    text: "Make a spellcast roll against a target within Far range. On a success, the target takes d6+1 magic damage using your Proficiency and the next time the target deals damage to an ally it is reduced by half. If you succeed with Fear, the target instead takes d10+1 magic damage using your Proficiency.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Make a <strong className="inline-strong">Spellcast Roll</strong> against a target within {FAR} range. On a success, the target takes <strong className="inline-strong">d6+1</strong> magic damage using your Proficiency and the next time the target deals damage to an ally it is reduced by half. If you succeed with Fear, the target instead takes <strong className="inline-strong">d10+1</strong> magic damage using your Proficiency.</p>
      </>
    ),
  },
  {
    name: 'Voice of Dread',
    level: 1,
    domain: 'Dread',
    type: 'Spell',
    recallCost: 0,
    text: "You can magically speak directly into the ears of a creature you can see. To torment them with your words, make a Spellcast Roll against them. On a success, they must mark a Stress and become temporarily Vulnerable.",
    rendered: (
      <>
        <p key={0} className="card-text-para">You can magically speak directly into the ears of a creature you can see. To torment them with your words, make a <strong className="inline-strong">Spellcast Roll</strong> against them. On a success, they must mark a {STRESS} and become temporarily {VULNERABLE}.</p>
      </>
    ),
  },
  {
    name: 'Umbral Veil',
    level: 1,
    domain: 'Dread',
    type: 'Ability',
    recallCost: 1,
    text: "Once per rest, when you roll with Fear, you can spend any number of Hope to place an equal number of tokens on this card, encasing yourself in a shadowy energy. After an attack roll is made against you, you can spend any number of tokens to give the attack roll a -1 penalty per token. On your next rest, remove all tokens from this card.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Once per rest, when you roll with Fear, you can spend any number of {HOPE} to place an equal number of tokens on this card, encasing yourself in a shadowy energy. After an attack roll is made against you, you can spend any number of tokens to give the attack roll a -1 penalty per token. On your next rest, remove all tokens from this card.</p>
      </>
    ),
  },

  // ── Level 2 ────────────────────────────────────────────
  {
    name: 'Hideous Retribution',
    level: 2,
    domain: 'Dread',
    type: 'Spell',
    recallCost: 2,
    text: "When an ally within Close range takes damage from a target you can see, you can make a Spellcast Reaction Roll against the target. On a success, mark a Stress to deal them d6 magic damage using your Proficiency.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When an ally within {CLOSE} range takes damage from a target you can see, you can make a <strong className="inline-strong">Spellcast Reaction Roll</strong> against the target. On a success, mark a {STRESS} to deal them <strong className="inline-strong">d6</strong> magic damage using your Proficiency.</p>
      </>
    ),
  },
  {
    name: 'Siphon Essence',
    level: 2,
    domain: 'Dread',
    type: 'Spell',
    recallCost: 1,
    text: "Make a Spellcast Roll against a target within Very Close range. On a success, once per long rest, the target takes d20 magic damage using your Proficiency. You clear a number of Hit Points equal to the number Hit Points the target marked from this attack. On a success with Fear, you gain a +1 bonus to your Proficiency for this attack.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Make a <strong className="inline-strong">Spellcast Roll</strong> against a target within {VERY_CLOSE} range. On a success, once per {LONG_REST}, the target takes <strong className="inline-strong">d20</strong> magic damage using your Proficiency. You clear a number of {HIT_POINT}s equal to the number of {HIT_POINT}s the target marked from this attack. On a success with Fear, you gain a +1 bonus to your Proficiency for this attack.</p>
      </>
    ),
  },
  {
    name: 'Terrify',
    level: 2,
    domain: 'Dread',
    type: 'Spell',
    recallCost: 1,
    text: "Make a Spellcast Roll against a target within Far range. On a success, the target marks 1d4 Stress and you can choose to make the target run one range away from you (Close to Far, Far to Very Far, etc). On a success with Fear, the target becomes temporarily Vulnerable.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Make a <strong className="inline-strong">Spellcast Roll</strong> against a target within {FAR} range. On a success, the target marks <strong className="inline-strong">1d4</strong> {STRESS} and you can choose to make the target run one range away from you (Close to Far, Far to Very Far, etc). On a success with Fear, the target becomes temporarily {VULNERABLE}.</p>
      </>
    ),
  },

  // ── Level 3 ────────────────────────────────────────────
  {
    name: 'Shared Trauma',
    level: 3,
    domain: 'Dread',
    type: 'Ability',
    recallCost: 1,
    text: "You can transfer suffering from one creature to another. Once per rest, mark any number of Hit Points on a willing creature within Melee range to clear an equal number of Hit Points on another willing creature within Melee range. You can choose yourself in place of either creature.",
    rendered: (
      <>
        <p key={0} className="card-text-para">You can transfer suffering from one creature to another. Once per rest, mark any number of {HIT_POINT}s on a willing creature within {MELEE} range to clear an equal number of {HIT_POINT}s on another willing creature within {MELEE} range. You can choose yourself in place of either creature.</p>
      </>
    ),
  },

  // ── Level 4 ────────────────────────────────────────────
  {
    name: 'Chains of Affliction',
    level: 4,
    domain: 'Dread',
    type: 'Ability',
    recallCost: 2,
    text: "Mark 2 Stress to temporarily Chain a target within Close range. When a Chained creature deals damage, the target of their attack reduces the number of Hit Points they mark by one. You can't have more than one creature Chained at a time.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Mark 2 {STRESS} to temporarily <strong className="inline-strong">Chain</strong> a target within {CLOSE} range. When a Chained creature deals damage, the target of their attack reduces the number of {HIT_POINT}s they mark by one. You can't have more than one creature Chained at a time.</p>
      </>
    ),
  },
  {
    name: 'Summon Horror',
    level: 4,
    domain: 'Dread',
    type: 'Spell',
    recallCost: 2,
    text: "Make a Spellcast Roll against a target within Far range. On a success, spend a Hope to call forth an otherworldly creature to attack them and deal d10 magic damage using your Proficiency. The target must succeed on a Reaction Roll (12) to steel themselves from the horror or mark 1d4 Stress. After making the attack, the creature dissipates.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Make a <strong className="inline-strong">Spellcast Roll</strong> against a target within {FAR} range. On a success, spend a {HOPE} to call forth an otherworldly creature to attack them and deal <strong className="inline-strong">d10</strong> magic damage using your Proficiency. The target must succeed on a {REACTION_ROLL} (12) to steel themselves from the horror or mark <strong className="inline-strong">1d4</strong> {STRESS}.</p>
        <p key={1} className="card-text-para">After making the attack, the creature dissipates.</p>
      </>
    ),
  },

  // ── Level 5 ────────────────────────────────────────────
  {
    name: 'Spectral Mist',
    level: 5,
    domain: 'Dread',
    type: 'Spell',
    recallCost: 0,
    text: "Mark a Stress to summon an eerie mist that turns you and any targets within Close range momentarily incorporeal. While a creature is incorporeal, they can move through solid objects and are immune to physical damage. A creature becomes corporeal again after they pass through a solid object or make an action roll. Otherwise, this effect drops at the end of the scene.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Mark a {STRESS} to summon an eerie mist that turns you and any targets within {CLOSE} range momentarily incorporeal. While a creature is incorporeal, they can move through solid objects and are immune to physical damage. A creature becomes corporeal again after they pass through a solid object or make an action roll. Otherwise, this effect drops at the end of the scene.</p>
      </>
    ),
  },
  {
    name: 'Dire Strike',
    level: 5,
    domain: 'Dread',
    type: 'Ability',
    recallCost: 1,
    text: "After making a successful attack, you can spend 2 Hope to leach power from the target. For each Hit Point your target marked from this attack, the GM loses a Fear.",
    rendered: (
      <>
        <p key={0} className="card-text-para">After making a successful attack, you can spend 2 {HOPE} to leach power from the target. For each {HIT_POINT} your target marked from this attack, the GM loses a {FEAR}.</p>
      </>
    ),
  },


  // ── Level 6 ────────────────────────────────────────────
  {
    name: 'Darkfire',
    level: 6,
    domain: 'Dread',
    type: 'Spell',
    recallCost: 2,
    text: "Make a Spellcast Roll against all adversaries within Close range. You can spend a Hope for any you succeed against, and they must make a Reaction Roll (14). On a failure, they take d8+6 magic damage using your Proficiency as they are engulfed in dark fire. On a success, they take half damage.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Make a <strong className="inline-strong">Spellcast Roll</strong> against all adversaries within {CLOSE} range. You can spend a {HOPE} for any you succeed against, and they must make a {REACTION_ROLL} (14). On a failure, they take <strong className="inline-strong">d8+6</strong> magic damage using your Proficiency as they are engulfed in dark fire. On a success, they take half damage.</p>
      </>
    ),
  },
  {
    name: 'Jump Scare',
    level: 6,
    domain: 'Dread',
    type: 'Ability',
    recallCost: 0,
    text: "When you deal magic damage to a target, you can mark a Stress to immediately teleport into Melee range of them. When you do, they become Vulnerable until they mark one or more Hit Points.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When you deal magic damage to a target, you can mark a {STRESS} to immediately teleport into {MELEE} range of them. When you do, they become {VULNERABLE} until they mark one or more {HIT_POINT}s.</p>
      </>
    ),
  },

  // ── Level 7 ────────────────────────────────────────────
  {
    name: 'Dread-Touched',
    level: 7,
    domain: 'Dread',
    type: 'Ability',
    recallCost: 2,
    text: "When 4 or more of the domain cards in your loadout are from the Dread domain, gain the following benefits: When you succeed with Fear, you can mark 2 Stress to keep the GM from gaining a Fear. Once per rest, when making an action roll, you can add a +1 bonus to the roll for each Fear token the GM has stored.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When 4 or more of the domain cards in your loadout are from the Dread domain, gain the following benefits:</p>
        <ul key={1}>
          <li>When you succeed with {FEAR}, you can mark 2 {STRESS} to keep the GM from gaining a {FEAR}.</li>
          <li>Once per rest, when making an action roll, you can add a +1 bonus to the roll for each {FEAR} token the GM has stored.</li>
        </ul>
      </>
    ),
  },
  {
    name: 'Wall of Hunger',
    level: 7,
    domain: 'Dread',
    type: 'Spell',
    recallCost: 2,
    text: "Succeed a Spellcast Roll (13) to create a visible wall of writhing, necrotic energy in a line between two points within Far range that lasts until you mark a Hit Point or cast this spell again. Any creatures inside the wall when it appears or who try to pass through the wall must mark 2 Stress, then make a Reaction Roll (16). On a failure, they are temporarily Restrained by the wall.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Succeed a <strong className="inline-strong">Spellcast Roll (13)</strong> to create a visible wall of writhing, necrotic energy in a line between two points within {FAR} range that lasts until you mark a {HIT_POINT} or cast this spell again. Any creatures inside the wall when it appears or who try to pass through the wall must mark 2 {STRESS}, then make a {REACTION_ROLL} (16). On a failure, they are temporarily <strong className="inline-strong">Restrained</strong> by the wall.</p>
      </>
    ),
  },

  // ── Level 8 ────────────────────────────────────────────
  {
    name: 'Dark Army',
    level: 8,
    domain: 'Dread',
    type: 'Spell',
    recallCost: 2,
    text: "Make a Spellcast Roll (14). Once per long rest, on a success you can summon a group of fiends that surround and move with you. Place 8 tokens on this card. When you deal damage to a target within Very Close range, you can spend a token to increase it by +1d8. Additionally, when you take damage, you can spend a token to reduce it by 1d8. Each time you spend a token, a fiend acts on your behalf, then disappears. Remove all tokens from this card on your next rest.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Make a <strong className="inline-strong">Spellcast Roll (14)</strong>. Once per {LONG_REST}, on a success you can summon a group of fiends that surround and move with you. Place 8 tokens on this card. When you deal damage to a target within {VERY_CLOSE} range, you can spend a token to increase it by <strong className="inline-strong">+1d8</strong>. Additionally, when you take damage, you can spend a token to reduce it by <strong className="inline-strong">1d8</strong>. Each time you spend a token, a fiend acts on your behalf, then disappears.</p>
        <p key={1} className="card-text-para">Remove all tokens from this card on your next rest.</p>
      </>
    ),
  },
  {
    name: 'Eldritch Flesh',
    level: 8,
    domain: 'Dread',
    type: 'Ability',
    recallCost: 1,
    text: "You embody the darkness you have dallied with. When this card is in your loadout: Gain a +1 bonus to your damage thresholds for each Stress you have marked. When you roll with Fear, you can spend 2 Hope to clear an Armor Slot.",
    rendered: (
      <>
        <p key={0} className="card-text-para">You embody the darkness you have dallied with. When this card is in your loadout:</p>
        <ul key={1}>
          <li>Gain a +1 bonus to your damage thresholds for each {STRESS} you have marked.</li>
          <li>When you roll with {FEAR}, you can spend 2 {HOPE} to clear an {ARMOR_SLOTS}.</li>
        </ul>
      </>
    ),
  },

  // ── Level 9 ────────────────────────────────────────────
  {
    name: 'Damnation',
    level: 9,
    domain: 'Dread',
    type: 'Spell',
    recallCost: 2,
    text: "Make a Spellcast Roll against a target within Far range. On a success, mark any number of Stress to roll an equal number of d20s, dealing magic damage equal to the total result. If the target is defeated as a result of this attack, all adversaries within Far range of the target mark a Stress.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Make a <strong className="inline-strong">Spellcast Roll</strong> against a target within {FAR} range. On a success, mark any number of {STRESS} to roll an equal number of <strong className="inline-strong">d20s</strong>, dealing magic damage equal to the total result. If the target is defeated as a result of this attack, all adversaries within {FAR} range of the target mark a {STRESS}.</p>
      </>
    ),
  },
  {
    name: 'Savor the Anguish',
    level: 9,
    domain: 'Dread',
    type: 'Ability',
    recallCost: 0,
    text: "When an adversary within Close range marks Stress or takes Severe damage, you can spend a Hope to clear a Stress or force the GM to lose a Fear.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When an adversary within {CLOSE} range marks {STRESS} or takes Severe damage, you can spend a {HOPE} to clear a {STRESS} or force the GM to lose a {FEAR}.</p>
      </>
    ),
  },

  // ── Level 10 ───────────────────────────────────────────
  {
    name: 'Invoke Torment',
    level: 10,
    domain: 'Dread',
    type: 'Ability',
    recallCost: 2,
    text: "Targets with all of their Stress marked take double damage from your attacks. Additionally, when you defeat a creature with all of its Stress marked, you clear a Stress.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Targets with all of their {STRESS} marked take double damage from your attacks.</p>
        <p key={1} className="card-text-para">Additionally, when you defeat a creature with all of its {STRESS} marked, you clear a {STRESS}.</p>
      </>
    ),
  },
  {
    name: 'Avatar of Terror',
    level: 10,
    domain: 'Dread',
    type: 'Ability',
    recallCost: 1,
    text: "Mark a Stress to transform into a creature fueled by fear. While in this form, your damage rolls gain a +1d6 bonus for each Fear the GM has. Additionally, gain a Hope when the GM uses a Fear feature on an adversary within Close range. You must spend a Hope to make an action roll while in this form. Otherwise, you drop out of this form.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Mark a {STRESS} to transform into a creature fueled by fear. While in this form, your damage rolls gain a <strong className="inline-strong">+1d6</strong> bonus for each {FEAR} the GM has. Additionally, gain a {HOPE} when the GM uses a Fear feature on an adversary within {CLOSE} range.</p>
        <p key={1} className="card-text-para">You must spend a {HOPE} to make an action roll while in this form. Otherwise, you drop out of this form.</p>
      </>
    ),
  },
];

export default dreadCards;
